import { motion } from 'framer-motion';
import { Leaf, Sun, Mountain, Users } from 'lucide-react';
import { useSiteSettings } from '../hooks/useSiteSettings';
import { SeoHead } from '../components/SeoHead';

export const ChiSiamoPage = () => {
  const { settings } = useSiteSettings();

  const fadeUp = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] }
  };

  const timeline = [
    { year: settings.timeline_0_year, title: settings.timeline_0_title, description: settings.timeline_0_desc },
    { year: settings.timeline_1_year, title: settings.timeline_1_title, description: settings.timeline_1_desc },
    { year: settings.timeline_2_year, title: settings.timeline_2_title, description: settings.timeline_2_desc },
    { year: settings.timeline_3_year, title: settings.timeline_3_title, description: settings.timeline_3_desc },
  ];

  const values = [
    { icon: Mountain, title: 'Terra Vulcanica',  description: 'Il suolo piroclastico del Vesuvio dona ai nostri pomodorini napoletani un sapore unico, irriproducibile altrove.' },
    { icon: Leaf,     title: 'Sostenibilità',    description: 'Coltiviamo nel rispetto dell\'ambiente con i metodi tradizionali dell\'agricoltura campana.' },
    { icon: Sun,      title: 'Autenticità',      description: 'Ogni prodotto racconta la storia della tradizione gastronomica vesuviana e campana.' },
    { icon: Users,    title: 'Famiglia',         description: 'Tre generazioni di agricoltori vesuviani unite dalla passione per la terra e per i prodotti tipici campani.' },
  ];

  return (
    <div data-testid="chi-siamo-page">
      <SeoHead
        title="Chi Siamo | Azienda Agricola ai Piedi del Vesuvio"
        description="Scopri la storia di Le Bontà del Vesuvio: azienda agricola familiare a Ercolano (NA). Tre generazioni di coltivatori nella terra vulcanica del Vesuvio, la stessa del Pomodoro del Piennolo DOP."
        keywords="azienda agricola vesuvio, chi siamo bontà del vesuvio, storia azienda agricola campana, terra vulcanica vesuvio, pomodorino napoletano ercolano, pomodoro del piennolo territorio"
        canonical="/chi-siamo"
      />

      {/* ── Hero compatto ── */}
      <section className="relative flex items-center" data-testid="about-hero">
        <div className="absolute inset-0">
          <img src={settings.chi_siamo_hero_image_url} alt="La nostra terra" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/55" />
        </div>
        <div className="relative container-custom pt-24 pb-6 md:pt-28 md:pb-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <span className="text-accent font-body text-xs uppercase tracking-widest mb-1 block">La Nostra Storia</span>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-white">Chi Siamo</h1>
          </motion.div>
        </div>
      </section>

      {/* ── Storia ── */}
      <section className="py-8 md:py-12" data-testid="story-section">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <motion.div {...fadeUp}>
              <span className="text-secondary font-body text-xs uppercase tracking-widest mb-2 block">La Nostra Famiglia</span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-text-DEFAULT mb-4">
                {settings.chi_siamo_story_title}
              </h2>
              <div className="space-y-3 font-body text-text-muted leading-relaxed text-sm md:text-base">
                <p>{settings.chi_siamo_story_p1}</p>
                <p>{settings.chi_siamo_story_p2}</p>
                <p>{settings.chi_siamo_story_p3}</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.7 }}
              className="relative"
            >
              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-lg">
                <img src={settings.chi_siamo_story_image_url} alt="I nostri agricoltori" className="w-full h-full object-cover" />
              </div>
              <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-primary/15 rounded-2xl -z-10" />
              <div className="absolute -top-4 -right-4 w-16 h-16 bg-accent/25 rounded-full -z-10" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── Valori ── */}
      <section className="py-8 md:py-12 bg-background-paper" data-testid="values-section">
        <div className="container-custom">
          <motion.div {...fadeUp} className="text-center mb-8">
            <span className="text-secondary font-body text-xs uppercase tracking-widest mb-1 block">I Nostri Valori</span>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-text-DEFAULT">Cosa ci rende unici</h2>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {values.map((value, i) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.08 }}
                className="flex flex-col gap-3 p-5 bg-white rounded-2xl border border-primary/10 hover:shadow-md transition-shadow"
              >
                <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <value.icon className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-heading text-base font-semibold text-text-DEFAULT mb-1">{value.title}</h3>
                  <p className="font-body text-text-muted text-xs sm:text-sm leading-relaxed">{value.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Timeline — griglia 2×2 ── */}
      <section className="py-6 md:py-10" data-testid="timeline-section">
        <div className="container-custom">
          <motion.div {...fadeUp} className="text-center mb-5">
            <span className="text-secondary font-body text-xs uppercase tracking-widest mb-1 block">Il Nostro Percorso</span>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-text-DEFAULT">La nostra storia nel tempo</h2>
          </motion.div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
            {timeline.map((item, i) => (
              <motion.div
                key={item.year || i}
                initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.45, delay: i * 0.07 }}
                className="bg-white rounded-xl border border-primary/10 p-4 shadow-sm"
              >
                <span className="text-secondary font-heading text-lg font-bold block">{item.year}</span>
                <h3 className="font-heading text-sm font-semibold text-text-DEFAULT mt-1 mb-1">{item.title}</h3>
                <p className="font-body text-text-muted text-xs leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Territorio ── */}
      <section className="py-8 md:py-12 bg-primary-dark text-white" data-testid="territory-section">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
              <span className="text-accent font-body text-xs uppercase tracking-widest mb-2 block">Il Territorio Vesuviano</span>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-white mb-4">
                {settings.chi_siamo_territory_title || 'La Terra Vulcanica del Vesuvio'}
              </h2>
              <div className="space-y-3 font-body text-white/80 leading-relaxed text-sm md:text-base">
                <p>{settings.chi_siamo_territory_p1}</p>
                <p>{settings.chi_siamo_territory_p2}</p>
                <p>{settings.chi_siamo_territory_p3}</p>
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
              <div className="aspect-video rounded-2xl overflow-hidden shadow-xl">
                <img src={settings.chi_siamo_territory_image_url} alt="Terra vulcanica ai piedi del Vesuvio — coltivazioni di pomodorini napoletani" className="w-full h-full object-cover" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── SEO: Il Pomodorino del Vesuvio e il Piennolo ── */}
      <section className="py-8 md:py-12 bg-background-paper" aria-label="Pomodorino Napoletano del Vesuvio e tradizione campana">
        <div className="container-custom max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.6 }}
          >
            <span className="text-secondary font-body text-xs uppercase tracking-widest mb-2 block text-center">Eccellenza Agroalimentare Campana</span>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-text-DEFAULT text-center mb-6">
              Pomodorino Napoletano: la Tradizione del Vesuvio
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
              <div className="bg-white rounded-xl p-5 border border-primary/10">
                <h3 className="font-heading font-semibold text-primary mb-2">
                  La Stessa Terra del Piennolo DOP
                </h3>
                <p className="font-body text-text-muted leading-relaxed">
                  La nostra azienda agricola si trova a Ercolano, nel cuore del territorio vulcanico del
                  <strong> Vesuvio</strong> che ha dato origine al celebre
                  <strong> Pomodoro del Piennolo del Vesuvio DOP</strong>.
                  Il suolo piroclastico, l'escursione termica e il microclima mediterraneo creano
                  le condizioni uniche che rendono i <strong>pomodorini vesuviani</strong> incomparabili
                  per profumo, dolcezza e conservabilità naturale.
                </p>
              </div>
              <div className="bg-white rounded-xl p-5 border border-primary/10">
                <h3 className="font-heading font-semibold text-primary mb-2">
                  Prodotti Tipici della Campania Felix
                </h3>
                <p className="font-body text-text-muted leading-relaxed">
                  Dal <strong>pomodorino napoletano</strong> fresco alle
                  <strong> conserve artigianali vesuviane</strong>, ogni nostro prodotto esprime
                  l'identità dell'<strong>agricoltura campana</strong> di qualità.
                  La stessa terra che produce il <strong>pomodoro del piennolo</strong>,
                  le albicocche del Vesuvio e i friarielli napoletani — un patrimonio gastronomico
                  unico nel panorama dei <strong>prodotti tipici italiani</strong>.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

    </div>
  );
};
